import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePlanner } from '../context/PlannerContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiUpload, FiUsers, FiCheckCircle, FiFileText } = FiIcons;

function AssessmentInput() {
  const navigate = useNavigate();
  const { state, dispatch } = usePlanner();
  const [assessmentType, setAssessmentType] = useState(state.assessmentType || '');

  const handleAssessmentTypeSelect = (type) => {
    setAssessmentType(type);
    dispatch({ type: 'SET_ASSESSMENT_TYPE', payload: type });

    // Navigate to appropriate form based on selection
    if (type === 'CORE') {
      navigate('/core-survey');
    } else if (type === 'QPS') {
      navigate('/qps-survey');
    } else if (type === 'DIBELS') {
      navigate('/dibels-survey');
    } else if (type === 'THRIVE') {
      navigate('/thrive-survey');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-body"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Home
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-heading">Step 1: Assessment Data Input</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Assessment Type Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-thrive-slate mb-4 font-heading">Choose Your Assessment Type</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => handleAssessmentTypeSelect('QPS')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                assessmentType === 'QPS' 
                  ? 'border-thrive-teal bg-thrive-mint/20 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <h3 className="font-semibold text-lg mb-2 font-body">Quick Phonics Screener (QPS)</h3>
              <p className="text-gray-600 font-body">12 skill areas with percentage accuracy scores</p>
              <p className="text-sm text-thrive-teal mt-2 font-body">✨ Individual student input form</p>
            </button>

            <button
              onClick={() => handleAssessmentTypeSelect('DIBELS')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                assessmentType === 'DIBELS' 
                  ? 'border-thrive-teal bg-thrive-mint/20 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <h3 className="font-semibold text-lg mb-2 font-body">DIBELS 8</h3>
              <p className="text-gray-600 font-body">7 measures including FSF, PSF, NWF, ORF, and Maze</p>
              <p className="text-sm text-thrive-teal mt-2 font-body">✨ Individual student input form</p>
            </button>

            <button
              onClick={() => handleAssessmentTypeSelect('CORE')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                assessmentType === 'CORE' 
                  ? 'border-thrive-teal bg-thrive-mint/20 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiFileText} className="text-thrive-teal" />
                <h3 className="font-semibold text-lg font-body">CORE Phonics Survey</h3>
              </div>
              <p className="text-gray-600 font-body">12 skill areas with raw score input</p>
              <p className="text-sm text-thrive-teal mt-2 font-body">✨ Individual student input form</p>
            </button>

            <button
              onClick={() => handleAssessmentTypeSelect('THRIVE')}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                assessmentType === 'THRIVE' 
                  ? 'border-thrive-teal bg-thrive-mint/20 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiFileText} className="text-thrive-teal" />
                <h3 className="font-semibold text-lg font-body">Thrive Literacy Corner Small Group Assessment</h3>
              </div>
              <p className="text-gray-600 font-body">25 question checklist with Yes/No responses</p>
              <p className="text-sm text-thrive-teal mt-2 font-body">✨ Individual student input form</p>
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default AssessmentInput;